import { ReactNode, useState, useCallback } from 'react';
import { ReferencesContext, ReferencesState } from './ReferencesContext';
import { useApi } from '../../api/useApi';

interface ReferencesProviderProps {
  children: ReactNode;
}

export const ReferencesProvider = ({ children }: ReferencesProviderProps) => {
  const api = useApi();
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const [references, setReferences] = useState<ReferencesState>({
    genders: [],
    insurances: [],
    examinationTypes: [],
    persons: [],
    employees: [],
    examiningPhysicians: [],
    applicants: [],
    paymentTypes: [],
    deathClassifications: [],
    diagnoses: [],
    toxicologists: [],
    providerDepartments: []
  });

  const loadReferences = useCallback(async () => {
    if (isLoaded || loading) {
      return;
    }

    setLoading(true);
    try {
      const [
        gendersResponse,
        insurancesResponse,
        examinationTypesResponse,
        personsResponse,
        employeesResponse,
        examiningPhysiciansResponse,
        applicantsResponse,
        paymentTypesResponse,
        deathClassificationsResponse,
        diagnosesResponse,
        toxicologistsResponse,
        providerDepartmentsResponse
      ] = await Promise.allSettled([
        api.references.genders.get({ limit: 100 }),
        api.references.insurances.get({ limit: 100 }),
        api.references.examinationTypes.get({}),
        api.references.persons.get({}),
        api.references.employees.get({}),
        api.references.examiningPhysicians.get({}),
        api.references.applicants.get({}),
        api.references.paymentTypes.get({}),
        api.references.deathClassifications.get({}),
        api.references.diagnoses.get({}),
        api.references.toxicologists.get({}),
        api.references.providerDepartments.get({})
      ]);

      // Safely get results, returns [] if request failed
      const getResults = (result: any) =>
        result.status === 'fulfilled' && result.value?.data?.results
          ? result.value.data.results
          : [];

      setReferences({
        genders: getResults(gendersResponse),
        insurances: getResults(insurancesResponse),
        examinationTypes: getResults(examinationTypesResponse),
        persons: getResults(personsResponse),
        employees: getResults(employeesResponse),
        examiningPhysicians: getResults(examiningPhysiciansResponse),
        applicants: getResults(applicantsResponse),
        paymentTypes: getResults(paymentTypesResponse),
        deathClassifications: getResults(deathClassificationsResponse),
        diagnoses: getResults(diagnosesResponse),
        toxicologists: getResults(toxicologistsResponse),
        providerDepartments: getResults(providerDepartmentsResponse)
      });

      setIsLoaded(true);
    } catch (error) {
      console.error('Chyba při načítání číselníků:', error);
    } finally {
      setLoading(false);
    }
  }, [api, isLoaded, loading]);

  return (
    <ReferencesContext.Provider value={{ references, loading, loadReferences }}>
      {children}
    </ReferencesContext.Provider>
  );
};
